import { useCallback, useMemo } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";

/**
 * Drop-in replacement for useState that syncs filter state with URL search params.
 *
 * @param {Object} defaults - The full filter object with default values.
 * @param {Object} [options]
 * @param {string[]} [options.exclude] - Keys to exclude from URL (e.g. route-derived or constant values).
 * @param {Function} [options.sanitise] - Optional function to sanitise the filter state after reading
 *   from URL. Receives the raw state and should return the corrected state. Use this to enforce
 *   permission-based restrictions (e.g. resetting branchFilter to "Mine" for users without access).
 * @returns {[Object, Function]} - [filterState, setFilterState] matching the useState API.
 *
 * Usage:
 *   const [filter, setFilter] = useUrlFilterState(
 *     { search: "", page: 1, pageSize: 25, type: status },
 *     { exclude: ["type", "pageSize"] }
 *   );
 */
export function useUrlFilterState(defaults, options = {}) {
  const navigate = useNavigate();
  // Values arrive already coerced to string/number/boolean by the query
  // decoder, so parseParams normalises back to strings before applying the
  // type of each default.
  const search = useRouterState({ select: (state) => state.location.search });
  const defaultsKey = JSON.stringify(defaults);
  const stableDefaults = useMemo(
    () => defaults,
    // Defaults are passed inline in callers; compare by value so route-derived defaults
    // (like status) still propagate without causing churn on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultsKey],
  );
  const excludeSet = useMemo(
    () => new Set(options.exclude || []),
    // Stringify to keep a stable reference — exclude list never changes at runtime
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(options.exclude)],
  );
  const sanitise = options.sanitise;

  // Helper: parse URL params into a state object
  const parseParams = useCallback(
    (params) => {
      const state = { ...stableDefaults };
      for (const key of Object.keys(stableDefaults)) {
        if (excludeSet.has(key)) continue;
        const urlValue = params[key];
        if (urlValue !== undefined) {
          const defaultVal = stableDefaults[key];
          if (typeof defaultVal === "number") {
            state[key] = Number(urlValue);
          } else if (typeof defaultVal === "boolean") {
            state[key] = String(urlValue) === "true";
          } else {
            state[key] = String(urlValue);
          }
        }
      }
      return sanitise ? sanitise(state) : state;
    },
    [excludeSet, sanitise, stableDefaults],
  );

  // Build the current filter state by merging URL params over defaults
  const filterState = useMemo(() => {
    return parseParams(search);
  }, [parseParams, search]);

  // Setter that supports both direct value and functional updater patterns
  const setFilterState = useCallback(
    (valueOrUpdater) => {
      navigate({
        to: ".",
        replace: true,
        search: (prevParams) => {
          // Reconstruct the previous filter state from current URL + defaults
          const prevState = parseParams(prevParams);

          // Compute next state
          let nextState =
            typeof valueOrUpdater === "function"
              ? valueOrUpdater(prevState)
              : valueOrUpdater;

          // Apply sanitisation to the next state as well
          if (sanitise) {
            nextState = sanitise(nextState);
          }

          // Build new search params — only include non-default, non-excluded values
          const newParams = {};
          for (const [key, value] of Object.entries(nextState)) {
            if (excludeSet.has(key)) continue;
            // Omit defaults to keep URL clean
            if (value !== stableDefaults[key]) {
              newParams[key] = String(value);
            }
          }
          return newParams;
        },
      });
    },
    [navigate, parseParams, sanitise, excludeSet, stableDefaults],
  );

  return [filterState, setFilterState];
}
